import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BUSINESS,
  SERVICE_OPTIONS,
  SERVICE_AREAS,
  MAP_EMBED,
  LOGO_URL,
} from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  phone: "",
  email: "",
  service: "",
  message: "",
};

export const ContactFooter = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleService = (value) => setForm((f) => ({ ...f, service: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill out name, phone, email and the service you need.");
      return;
    }

    setSubmitting(true);
    const text =
      `Hi Nathan, I'd like to enquire about my pond/garden.\n\n` +
      `Name: ${form.name}\n` +
      `Phone: ${form.phone}\n` +
      `Email: ${form.email}\n` +
      `Service: ${form.service}\n` +
      `Message: ${form.message || "-"}`;
    const waUrl = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(text)}`;
    // Open WhatsApp within the click gesture so the browser doesn't block it
    window.open(waUrl, "_blank", "noopener");
    // Save a backup copy to the database (non-blocking)
    axios.post(`${API}/leads`, form).catch(() => {});
    setDone(true);
    setForm(initialState);
    setSubmitting(false);
    toast.success("Opening WhatsApp — just tap send to reach Nathan.");
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-green-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Contact info */}
          <div>
            <span className="inline-block text-green-400 font-semibold text-sm uppercase tracking-widest">
              Get in touch
            </span>
            <h2
              data-testid="contact-headline"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Get in touch about your pond or garden.
            </h2>
            <p className="mt-5 text-white/70 text-base lg:text-lg leading-relaxed max-w-md">
              Tell us about your pond or garden. We&apos;ll come out, take a proper look
              and talk through the work and pricing. We cover properties within
              approximately 30 minutes&apos; travel of us.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`tel:${BUSINESS.phoneTel}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-green-600 group-hover:border-green-600 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Call</div>
                  <div className="text-lg font-semibold">{BUSINESS.phone}</div>
                </div>
              </a>

              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-green-600 group-hover:border-green-600 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Email</div>
                  <div className="text-base font-semibold break-all">{BUSINESS.email}</div>
                </div>
              </a>

              <div className="flex items-start gap-4" data-testid="contact-areas">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">
                    Service Areas
                  </div>
                  <div className="text-base flex flex-wrap gap-x-2">
                    {SERVICE_AREAS.join(" · ")}
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-10 rounded-2xl overflow-hidden border border-white/10 shadow-lg">
              <iframe
                title="Service area map — East Grinstead, West Sussex"
                data-testid="contact-map"
                src={MAP_EMBED}
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div
            data-testid="contact-form-card"
            className="bg-white text-green-950 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl shadow-green-950/40"
          >
            {done ? (
              <div data-testid="contact-form-success" className="text-center py-10">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="mt-5 text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                  WhatsApp is opening&hellip;
                </h3>
                <p className="mt-3 text-slate-600">
                  Just tap <strong>send</strong> in WhatsApp and your enquiry reaches Nathan.
                  If it didn&apos;t open, message or call us directly.
                </p>
                <a href={`tel:${BUSINESS.phoneTel}`}>
                  <Button
                    data-testid="contact-success-call-cta"
                    className="mt-6 bg-green-700 hover:bg-green-800 rounded-full"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call {BUSINESS.phone}
                  </Button>
                </a>
                <button
                  type="button"
                  data-testid="contact-form-reset"
                  onClick={() => setDone(false)}
                  className="block mx-auto mt-6 text-sm text-slate-500 hover:text-green-700 underline"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Send us an enquiry
                </h3>
                <p className="text-sm text-slate-500 -mt-3">
                  Sends your enquiry straight to our WhatsApp.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange("name")}
                      className="mt-1 h-11"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      data-testid="contact-input-phone"
                      placeholder="07…"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      className="mt-1 h-11"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={handleChange("email")}
                    className="mt-1 h-11"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="service">Service needed</Label>
                  <Select value={form.service} onValueChange={handleService}>
                    <SelectTrigger
                      id="service"
                      data-testid="contact-select-service"
                      className="mt-1 h-11"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`contact-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    placeholder="Tell us about your pond or garden and the job…"
                    value={form.message}
                    onChange={handleChange("message")}
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full h-12 text-base font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send enquiry on WhatsApp
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting you agree to be contacted about your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <footer data-testid="site-footer" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row gap-4 items-center justify-between text-sm text-white/60">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white overflow-hidden">
              <img src={LOGO_URL} alt="Pond & Garden logo" className="w-10 h-10 object-cover" />
            </span>
            <span>
              © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
            </span>
          </div>
          <div className="text-white/50">{BUSINESS.tagline}</div>
        </div>
      </footer>
    </section>
  );
};

export default ContactFooter;
